import { TimeInForceType } from '@/compiled_proto/com/celertech/positionmanager/api/enums/TimeInForceTypeProto';
import { NetPositionEvent } from '@/compiled_proto/com/celertech/positionmanager/api/pnl/PostionManagerServiceProto';
import Divider from '@/components/common/Divider';
import Totals from '@/components/form/components/Totals';
import { closePositionSchema } from '@/components/form/schema/closePositionSchema';
import { MarketOrderFormInput, MarketOrderFormValues } from '@/components/form/schema/marketOrderSchema';
import ExecutionInputController from '@/components/inputs/ExecutionInputController';
import Select from '@/components/inputs/Select';
import { formatOrderMessage } from '@/helpers/orderHelper';
import { createLumeFxRestClient } from '@/services/LumeFxService';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { OrderPrefillValues } from '@/state/reducers/orderSlice';
import { selectSettings } from '@/state/reducers/settingSlice';
import { LumeFxOrderType, LumeFxSide, LumeFxTimeInForce } from '@/types/lumeFx';
import useCSSVariables from '@/utils/hooks/useCSSVariables';
import { useCurrency } from '@/utils/hooks/useCurrency';
import { useInstrument } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import { useMediaQuery } from '@/utils/hooks/useMediaQuery';
import useOrderBook from '@/utils/hooks/useOrderBook';
import useTotals from '@/utils/hooks/useTotals';
import { useLucera } from '@/utils/providers/LuceraProvider';
import { NopPosition, Positions } from '@/utils/providers/PositionsProvider';
import { Transition } from '@headlessui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { LuAlertTriangle } from 'react-icons/lu';
import CloseSubmit from '../form/components/CloseSubmit';
import FormBody from '../form/components/FormBody';
import FormFooter from '../form/components/FormFooter';
import NumberInputDisplay from '../inputs/NumberInputDisplay';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from './/Modal';

const durationOptions: { label: keyof typeof TimeInForceType; value: TimeInForceType }[] = [
    { label: 'IOC', value: TimeInForceType.IOC },
    { label: 'FOK', value: TimeInForceType.FOK }
];

const defaultValues: Partial<MarketOrderFormInput> = {
    quantity: null,
    duration: durationOptions[0]
};

interface ClosePositionModalProps extends ModalProps {
    debouncedNopPositions?: Positions;
    debouncedOpenPositions?: NetPositionEvent[];
    prefillValues?: OrderPrefillValues;
    setPrefillValues?: React.Dispatch<React.SetStateAction<OrderPrefillValues | undefined>>;
}

const luceraRestURL = window.config?.lucera?.rest;

export default function ClosePositionModal(props: ClosePositionModalProps) {
    const { opened, handlers, debouncedNopPositions, debouncedOpenPositions, prefillValues, setPrefillValues } = props;
    const instrument = prefillValues?.instrument || { show: '', celer: '', netdania: '' };

    const lang = useLanguage();
    const variables = useCSSVariables();

    const largeHeight = useMediaQuery('(min-height: 900px)');

    const settings = useAppSelector(selectSettings);
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const instrumentConfig = useInstrument(instrument.celer);

    const brandRed = useMemo(() => variables['--color-brand-red'], [variables]);
    const [ccy1, ccy2] = useMemo(() => instrument.celer.split('/') || [], [instrument]);

    const [side, setSide] = useState<LumeFxSide>(LumeFxSide.BUY);
    const [currency, setCurrency] = useState<string>(ccy1);
    const [positionChangedError, setPositionChangedError] = useState(false);

    const isCcy2Order = useMemo(() => currency === ccy2, [currency, ccy2]);
    const currentPosition = useMemo(() => {
        if (debouncedNopPositions) {
            const flattenedNopPositions = Object.values(debouncedNopPositions).flat();
            const position = flattenedNopPositions.find(
                (nop) => (nop?.instrumentWithDigitalAssetName || nop?.instrumentName) === instrument.show
            ) as NopPosition;

            return position?.amount;
        } else if (debouncedOpenPositions) {
            const position = debouncedOpenPositions.find(
                (pos) => pos.positionUpdate?.securityCode === instrument.celer
            );
            return position?.positionUpdate?.netQtyPosition;
        }
        return null;
    }, [debouncedNopPositions, debouncedOpenPositions, instrument]);

    const ccy1Config = useCurrency(ccy1);
    const ccy2Config = useCurrency(ccy2);

    const activeCurrencyConfig = useMemo(
        () => (!isCcy2Order ? ccy1Config : ccy2Config),
        [isCcy2Order, ccy1Config, ccy2Config]
    );

    const { asks, bids, bestPrice, settlementDate } = useOrderBook(instrument.celer || '', side, isCcy2Order);

    const orderMessage = useMemo(
        () =>
            formatOrderMessage({
                side,
                isCcy2Order,
                ccy1Config,
                ccy2Config,
                pairMapToUse: instrument,
                instrumentConfig
            }),
        [side, isCcy2Order, ccy1Config, ccy2Config]
    );

    const { jwt } = useLucera();

    const lumeFxRestClient = createLumeFxRestClient({
        baseUrl: luceraRestURL,
        authorization: jwt
    });

    const form = useForm<MarketOrderFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(closePositionSchema({}))
    });

    const {
        watch,
        reset,
        handleSubmit,
        formState: { isSubmitting, isValid }
    } = form;

    const totals = useTotals({
        form,
        pairMap: instrument,
        side,
        ccy1: instrumentConfig?.type === 'Future' ? instrumentConfig?.config?.ccy2 || '' : ccy1,
        ccy2: instrumentConfig?.type === 'Future' ? instrumentConfig?.config?.ccy2 || '' : ccy2,
        ccy2Order: isCcy2Order,
        bids,
        asks
    });

    const quantity = watch('quantity');

    const onSubmit = async (data: MarketOrderFormInput, e) => {
        const dataValidated = data as MarketOrderFormValues;
        if (dataValidated.quantity && credentials) {
            if (currentPosition !== prefillValues?.netQtyPosition) {
                setPositionChangedError(true);
                return;
            }
            handlers.close();
            lumeFxRestClient.placeOrder({
                Account: '123',
                Symbol: instrument?.celer,
                TimeInForce: dataValidated.duration.value as LumeFxTimeInForce,
                OrdType: LumeFxOrderType.Market,
                OrderQty: (dataValidated.quantity || 0).toString(),
                ClOrdID: crypto.randomUUID(),
                Side: side
            });
        }
    };

    const onError = (errors) => console.error(errors);

    const handleResetToNewPosition = useCallback(() => {
        if (currentPosition) {
            setPrefillValues?.({
                ...prefillValues,
                netQtyPosition: currentPosition
            } as OrderPrefillValues);
            setPositionChangedError(false);
            setSide((currentPosition || 0) > 0 ? LumeFxSide.SELL : LumeFxSide.BUY);
            reset({
                ...defaultValues,
                duration: settings.trading.defaultMarketOrderDuration,
                quantity: Math.abs(currentPosition)
            });
        }
    }, [reset, currentPosition, settings]);

    useEffect(() => {
        if (opened && prefillValues) {
            setCurrency(ccy1);
            setSide((prefillValues.netQtyPosition || 0) > 0 ? LumeFxSide.SELL : LumeFxSide.BUY);
            reset({
                ...defaultValues,
                duration: settings.trading.defaultMarketOrderDuration,
                quantity: Math.abs(prefillValues.netQtyPosition)
            });
        } else {
            setPositionChangedError(false);
        }
    }, [opened]);

    return (
        <Modal {...props} size="max-w-lg">
            <div className="h-full flex flex-col lg:block lg:h-auto">
                <ModalHeader>
                    <ModalTitle>{lang.orderForm.closePosition}</ModalTitle>
                    <ModalClose handlers={handlers} />
                </ModalHeader>
                <Divider side={side} />
                <div className="flex flex-col flex-1 basis-0 h-full lg:h-auto lg:block w-full text-brand-text">
                    <FormProvider {...form}>
                        <form
                            onSubmit={handleSubmit(onSubmit, onError)}
                            className={cn('relative flex flex-col h-full w-full text-brand-text', {
                                'lg:min-h-[450px]': !largeHeight,
                                'lg:min-h-[500px]': largeHeight
                            })}>
                            <FormBody side={side} mode="Modal">
                                <div className="flex justify-between items-center">{orderMessage}</div>
                                {brandRed && (
                                    <Transition
                                        as={Fragment}
                                        show={positionChangedError}
                                        enter="transition-all ease-out duration-200"
                                        enterFrom="opacity-0 -translate-y-1"
                                        enterTo="opacity-100 translate-y-0"
                                        leave="transition-all ease-in duration-150"
                                        leaveFrom="opacity-100 translate-y-0"
                                        leaveTo="opacity-0 -translate-y-1">
                                        <div
                                            className={cn(
                                                'rounded-md border overflow-hidden',
                                                `border-[${brandRed}]/40 bg-[${brandRed}]/10`
                                            )}>
                                            <div className="p-3 space-y-2.5">
                                                <div className="flex items-start gap-2">
                                                    <LuAlertTriangle
                                                        className={cn(
                                                            'mt-[2px w-3.5 h-3.5 flex-shrink-0',
                                                            `text-[${brandRed}]`
                                                        )}
                                                    />
                                                    <div className="text-[13px] text-brand-text leading-snug">
                                                        <div className="font-medium">
                                                            {lang.orderForm.closeOrderWasNotSubmitted}
                                                        </div>
                                                        <div className="text-brand-neutral-400 text-[12px] mt-1">
                                                            <div>
                                                                {lang.orderForm.thePositionChangedBeforeExecution},{' '}
                                                                {lang.orderForm.currentNetPosition}:{' '}
                                                                <span className="text-brand-text">
                                                                    {currentPosition}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 pl-[23px]">
                                                    <button
                                                        type="button"
                                                        onClick={handleResetToNewPosition}
                                                        className={cn(
                                                            'px-3 py-1.5 rounded text-[12px] font-medium text-brand-neutral-100 transition-colors',
                                                            `bg-[${brandRed}]/20 hover:bg-[${brandRed}]/30 border border-[${brandRed}]/40`
                                                        )}>
                                                        {lang.orderForm.resetToNewPosition}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setPositionChangedError(false)}
                                                        className="px-3 py-1.5 bg-transparent rounded text-xs font-medium text-brand-neutral-300 hover:text-brand-text transition-colors">
                                                        {lang.notifications.dismiss}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </Transition>
                                )}
                                <ExecutionInputController name="duration" label={`${lang.orderForm.duration} *`}>
                                    <Select type="execution" options={durationOptions} />
                                </ExecutionInputController>
                                <ExecutionInputController
                                    name="quantity"
                                    label={`${lang.orderForm.quantity} *`}
                                    value={quantity || undefined}>
                                    <NumberInputDisplay
                                        placeholder={lang.orderForm.quantity}
                                        currency={activeCurrencyConfig.show}
                                        disabled
                                    />
                                </ExecutionInputController>
                                <Totals {...totals} />
                            </FormBody>
                            <Divider side={side} />
                            <FormFooter mode="Modal">
                                <CloseSubmit
                                    side={side}
                                    isValid={isValid && !positionChangedError}
                                    disabled={
                                        isSubmitting || !isValid || !instrumentConfig.type || positionChangedError
                                    }
                                    noInstrumentConfig={!instrumentConfig.type}
                                    noSettlementDate={!settlementDate}
                                    className="left-6"
                                />
                            </FormFooter>
                        </form>
                    </FormProvider>
                </div>
            </div>
        </Modal>
    );
}
